/**
 * Hit reactions for ordinary blows (lane C0-HIT-REACT): the lock-form receive motions (render/rig/receive.ts) played on
 * anyone with the rig who takes a landed blow, picked by the blow's weight (data/hitWeight.ts `hitWeightOf`):
 * - light: a flinch out to `rcvReel` (the first blow of `rcvHit`) and back to the stance;
 * - medium: the same, out to `rcvSag`, a little slower;
 * - heavy (a power move, a break that lands): thrown back (`rcvKnock`) when struck from the front, whirled round
 *   (`rcvSpin`) when struck from the side or behind; a won interrupt always whirls; both then `rcvUp`;
 * - a light or medium blow that leaves a stun: a stagger (`rcvHit`, held on `rcvStagger`, `rcvUp`);
 * - a guard break / shatter: down on a knee (`rcvKneel`), held, then up from it (`rcvUpKneel`);
 * - the stagger, the kneel and a heavy throw that stuns last exactly the target's `stunT` at the event (the rules' stun,
 *   duel audit 2026-10-11 #7a): the hold on the shared key takes the slack, a short stun squeezes the way down and up;
 * - every reaction starts once the per-pair hit-stop (render/hitStop.ts, audit #8) lets go;
 * - death: the knees buckle, then over backwards (`rcvDie`; monsters the quicker `rcvFall`), lying with a knee up.
 *
 * Picture only: nothing here touches the World. The motion's lift, turn and lie are shot channels (the renderer's `dy`,
 * view turn and `rot`); the entity's position is the rules' own. A reaction yields at once to the figure's own actions
 * (strike, guard, cast, dash, spin, ride, the air, walking when not stunned) and is dropped for good when it does.
 * Rapid light blows never restart the flinch from the stance: each pushes it back out from wherever it is, so a flurry
 * holds the figure near the recoil instead of stuttering. A heavier reaction is not cut by a lighter blow.
 * Know-how: docs/research/C0_HIT_REACT.md (and C0_RECEIVE.md for the motions).
 */
import { hitWeightOf, type Entity, type World, type WorldEvent } from '@nine/shared';
import { eased, type Ease } from './motions/kit';
import { hitStopOf } from './hitStop';
import { knockReactFrame } from './knockReact';
import { MOTIONS, RECEIVE_CHANNELS, type ReceiveMotionId } from './rig/receive';
import type { ReceiveFrame } from './rig/receivePlay';

type Kind = 'light' | 'medium' | 'stagger' | 'knock' | 'spin' | 'kneel';

/** One stretch of a reaction: motion `id` from `u0` to `u1` over `dur` seconds. */
interface Seg {
  id: ReceiveMotionId;
  dur: number;
  u0: number;
  u1: number;
}

const seg = (id: ReceiveMotionId, dur: number, u0 = 0, u1 = 1): Seg => ({ id, dur, u0, u1 });

/**
 * The longer reactions, in order; joins are on shared keys (`rcvStagger`, `rcvKneelHold`), so no jump between them. The
 * middle stretch (u0 = u1) is the hold that `sized` stretches or drops to match the rules' stun.
 */
const SEQ: Readonly<Record<'stagger' | 'knock' | 'spin' | 'kneel', readonly Seg[]>> = {
  stagger: [seg('rcvHit', 0.5), seg('rcvHit', 0, 1, 1), seg('rcvUp', 0.45)],
  knock: [seg('rcvKnock', 0.56), seg('rcvKnock', 0, 1, 1), seg('rcvUp', 0.46)],
  spin: [seg('rcvSpin', 0.6), seg('rcvSpin', 0, 1, 1), seg('rcvUp', 0.44)],
  kneel: [seg('rcvKneel', 0.5), seg('rcvKneel', 0.22, 1, 1), seg('rcvUpKneel', 0.5)],
};

/** Shortest a stretch may be squeezed to (s): below this the pose no longer reads. */
const MIN_PART = 0.18;

/**
 * The stretches of `kind` lasting exactly `len` s (the rules' stun left after the hit-stop; duel audit #7a): the hold
 * takes up any extra; a short stun squeezes the way in and the way up alike, never under `MIN_PART` each. `len` 0 (no
 * stun: a throw without a stun) keeps the natural lengths.
 */
function sized(kind: keyof typeof SEQ, len: number): readonly Seg[] {
  const [a, h, b] = SEQ[kind] as readonly [Seg, Seg, Seg];
  if (len <= 0) return [a, h, b];
  const nat = a.dur + b.dur;
  if (len >= nat) return [a, { ...h, dur: len - nat }, b];
  const k = Math.max((2 * MIN_PART) / nat, len / nat);
  return [{ ...a, dur: a.dur * k }, { ...h, dur: 0 }, { ...b, dur: b.dur * k }];
}

/** A light or medium blow that leaves a stun longer than this (s) staggers instead of flinching. */
const STAGGER_FROM = 0.3;

/** The flinch: out to key `peak` of `rcvHit` (8 keys, so u = key / 7) in `out` s, back to the stance in `back` s. */
const FLINCH = {
  // Light: a small recoil, halfway between the brace and the snap (QA HIT-REACT-R1: key 3 crouched as deep as a break).
  light: { peak: 1.5, out: 0.07, back: 0.2 },
  medium: { peak: 4, out: 0.11, back: 0.32 },
} as const;

/** How heavy each kind is: a lighter blow never cuts a heavier reaction. */
const RANK: Readonly<Record<Kind, number>> = { light: 0, medium: 1, stagger: 2, spin: 2, knock: 2, kneel: 3 };

/** A heavy reaction is not restarted by another heavy blow sooner than this (s): no stutter in a combo. */
const HEAVY_GAP = 0.25;

/**
 * Death: the fall's length (s); monsters fade out over 0.6 s (renderer), so theirs lands sooner. Everyone else takes
 * `rcvDie`: about the first third on the knees (ns-art HIT-REACT death fix 1), the rest going over and lying.
 */
const FALL = { monster: 0.5, other: 1.5 };

/**
 * The fall's time warp: the first `TOPPLE_T` of the fall goes over to the flat-in-the-air key (u = 3/8), so the topple
 * shows its in-between poses instead of dropping in one step (QA HIT-REACT-R1); the impact, bounce and lie take the rest.
 */
const TOPPLE_T = 0.5;
const TOPPLE_U = 3 / 8;
function fallU(x: number): number {
  const k = Math.max(0, Math.min(1, x));
  return k < TOPPLE_T ? (k / TOPPLE_T) * TOPPLE_U : TOPPLE_U + ((k - TOPPLE_T) / (1 - TOPPLE_T)) * (1 - TOPPLE_U);
}

interface React {
  kind: Kind;
  /** When this run of it started (world s). */
  at: number;
  /** Flinch only: how far out (0–1) it was when the latest blow pushed it out again. */
  x0: number;
  /** When it ends by itself. */
  end: number;
  /** Cut by the figure's own action: no longer drawn, but still owns the blow (no hurt still in its place). */
  cut: boolean;
  /** Heavy, kneel and stagger: the stretches, sized to the rules' stun (`sized`). */
  segs: readonly Seg[];
}

const live = new Map<number, React>();

/** Flinch position (0 = stance, 1 = the recoil peak) `d` s into a run started at `x0`. */
function flinchX(kind: 'light' | 'medium', x0: number, d: number): number {
  const f = FLINCH[kind];
  const tOut = (1 - x0) * f.out;
  if (d < tOut) return x0 + (1 - x0) * eased('out', d / Math.max(1e-6, tOut));
  return 1 - eased('inOut', Math.min(1, (d - tOut) / f.back));
}

function flinchEnd(kind: 'light' | 'medium', x0: number): number {
  return (1 - x0) * FLINCH[kind].out + FLINCH[kind].back;
}

const seqLen = (s: readonly Seg[]) => s.reduce((a, x) => a + x.dur, 0);

/**
 * Starts or pushes the reaction for a landed blow. Called from effects.handle for every `hit` event, after the step that
 * landed it, so the target's `stunT` is the rules' stun for this blow. The reaction starts when the hit-stop hold ends.
 */
export function hitReactOnHit(ev: Extract<WorldEvent, { type: 'hit' }>, w: World): void {
  const tgt = w.get(ev.tgtId);
  if (!tgt || tgt.dead) return;
  const hw = hitWeightOf(ev.skillId, ev.outcome);
  const shatter = ev.outcome === 'guardBreak' || (ev.outcome as string) === 'guardShatter';
  if (!shatter && (!hw || hw.cls === 'guard')) return;
  const stun = Math.max(0, tgt.stunT);
  let kind: Kind;
  if (shatter) kind = 'kneel';
  else if (hw!.cls === 'heavy') {
    const src = w.get(ev.srcId);
    let front = true;
    if (src) {
      const dx = src.x - tgt.x;
      const dy = src.y - tgt.y;
      const l = Math.hypot(dx, dy);
      if (l > 1e-6) front = (dx * tgt.dirX + dy * tgt.dirY) / (l * (Math.hypot(tgt.dirX, tgt.dirY) || 1)) > 0.35;
    }
    kind = ev.outcome === 'interrupt' || !front ? 'spin' : 'knock';
  } else if (stun > STAGGER_FROM) kind = 'stagger';
  else kind = hw!.cls === 'medium' ? 'medium' : 'light';
  const hold = hitStopOf(ev.skillId, ev.outcome);
  const t = w.time;
  const at = t + hold;
  const cur = live.get(tgt.id);
  const running = cur && !cur.cut && t < cur.end;
  if (running && RANK[cur.kind] > RANK[kind]) return;
  if (running && (cur.kind === 'light' || cur.kind === 'medium') && (kind === 'light' || kind === 'medium')) {
    // Pushed out again from where it is: never back to the stance first.
    const x0 = t >= cur.at ? flinchX(cur.kind, cur.x0, t - cur.at) : cur.x0;
    const k = RANK[kind] > RANK[cur.kind] ? kind : cur.kind;
    live.set(tgt.id, { kind: k, at, x0, end: at + flinchEnd(k, x0), cut: false, segs: [] });
    return;
  }
  if (running && cur.kind === kind && t - cur.at < HEAVY_GAP) return;
  if (kind === 'light' || kind === 'medium') {
    live.set(tgt.id, { kind, at, x0: 0, end: at + flinchEnd(kind, 0), cut: false, segs: [] });
  } else {
    // Exactly the rules' stun from the blow: the hold is part of it.
    const segs = sized(kind, stun > 0 ? Math.max(0, stun - hold) : 0);
    live.set(tgt.id, { kind, at, x0: 0, end: at + seqLen(segs), cut: false, segs });
  }
  if (live.size > 256) {
    for (const [id, r] of live) if (r.end < t) live.delete(id);
  }
}

/** The figure is doing something of its own: the reaction gives way at once. */
function ownAction(e: Entity): boolean {
  return (
    e.swingT > 0 ||
    e.blocking ||
    !!e.cast ||
    e.dashT > 0 ||
    e.spinT > 0 ||
    !!e.mount ||
    e.z > 0.5 ||
    e.meditating ||
    (e.moving && e.stunT <= 0)
  );
}

/** A channel's value `u` (0–1) through a motion, blended with the motion's own eases (as receivePlay.ts does). */
function channel(vals: readonly number[] | undefined, ease: readonly Ease[], u: number): number {
  if (!vals?.length) return 0;
  const n = vals.length;
  const x = Math.max(0, Math.min(1, u)) * (n - 1);
  const i = Math.min(n - 2, Math.floor(x));
  const k = eased(ease[i] ?? 'inOut', x - i);
  return vals[i]! + (vals[i + 1]! - vals[i]!) * k;
}

function frame(id: ReceiveMotionId, u: number): ReceiveFrame {
  const m = MOTIONS.find((x) => x.id === id)!;
  const ch = RECEIVE_CHANNELS[id];
  const uu = Math.max(0, Math.min(1, u));
  return {
    id,
    u: uu,
    rot: channel(ch?.rot, m.ease, uu),
    lift: channel(ch?.lift, m.ease, uu),
    turn: Math.round(channel(ch?.turn, m.ease, uu)),
  };
}

/**
 * Duel v3 P1: recovery after a move, shown as a slump (the first key of `rcvHit`; a whiff goes further, the overextended
 * hold) that eases in, holds, and eases out as the recovery ends. Fed once a frame by passes/duelRecoveryTell.ts.
 */
const recov = new Map<number, { t: number; len: number; whiff: boolean }>();
export function hitReactRecovery(id: number, info: { t: number; len: number; whiff: boolean } | null): void {
  if (info) recov.set(id, info);
  else recov.delete(id);
}
const SLUMP = { hit: 1 / 7, whiff: 2.5 / 7, in: 0.06, out: 0.12 };

function slumpFrame(e: Entity): ReceiveFrame | null {
  const rv = recov.get(e.id);
  if (!rv || e.dead || ownAction(e)) return null;
  const k = Math.min(1, rv.t / SLUMP.in, Math.max(0, rv.len - rv.t) / SLUMP.out);
  if (k <= 0) return null;
  return frame('rcvHit', (rv.whiff ? SLUMP.whiff : SLUMP.hit) * eased('inOut', k));
}

/** The reaction frame for `e` at world time `t`, or null (none, over, or given way to the figure's own action). */
export function hitReactFrame(e: Entity, t: number): ReceiveFrame | null {
  const knocked = knockReactFrame(e.id); // กระเด็น / ล้ม / ลุก wins (render/knockReact.ts)
  if (knocked) return knocked;
  const r = live.get(e.id);
  if (!r) return slumpFrame(e);
  if (r.cut) return null;
  if (t >= r.end || e.dead || t < r.at - 1) {
    live.delete(e.id);
    return null;
  }
  // Inside the hit-stop hold (the renderer shows the held contact frame): the reaction has not begun.
  if (t < r.at) return null;
  if (ownAction(e)) {
    r.cut = true;
    return null;
  }
  const d = t - r.at;
  if (r.kind === 'light' || r.kind === 'medium')
    return frame('rcvHit', (flinchX(r.kind, r.x0, d) * FLINCH[r.kind].peak) / 7);
  let left = d;
  const s = r.segs;
  for (const g of s) {
    if (left < g.dur) return frame(g.id, g.u0 + (g.u1 - g.u0) * (left / g.dur));
    left -= g.dur;
  }
  const last = s[s.length - 1]!;
  return frame(last.id, last.u1);
}

/** A reaction owns this blow (drawn, or cut by the figure's own action): the old hurt still stays out of its way. */
export function hitReactOwns(id: number, t: number): boolean {
  const r = live.get(id);
  return !!r && t >= r.at - 1 && t < r.end;
}

/** Death on the rig, `d` s after it: knees first, then over backwards (`rcvDie`); a monster just topples (`rcvFall`). */
export function hitReactDeath(e: Entity, d: number): ReceiveFrame {
  if (e.kind === 'monster') return frame('rcvFall', fallU(Math.max(0, d) / FALL.monster));
  return frame('rcvDie', Math.max(0, d) / FALL.other);
}

/** A dead player's opacity `d` s after the death: whole through the fall, then easing to the old ghost (0.35). */
export function hitReactGhost(d: number): number {
  const k = Math.max(0, Math.min(1, (d - FALL.other - 0.15) / 0.4));
  return 1 - 0.65 * k;
}
